import { useEffect, useState } from 'react';

import {
    NOTIFICATION_API_LIMIT,
    useGetNotificationsQuery,
    useLazyGetNotificationsAfterQuery,
    useSetBookmarkMutation,
    useUpdateNotificationMutation,
} from '../services/notificationsApi';
import { getLatestTimestampIgnoreReadStatus } from '../../utils/timestampUtil';
import { useUserId } from '../../configuration/tokenHandling/tokenSlice';
import { useLocale } from '../../configuration/lang/langSlice';
import { config } from '../../config';
import { useRemoteMessage } from '../widget/useRemoteMessage';
import type { Notification } from '../services/notificationsTypes';
import { useAfterTag, useExternalRefetchTrigger, useFetchedNotifications, useHasNext } from './notificationSlice';
import { dispatchMsgToParentIFrame, maxNotificationLevel, notificationCount } from '../../utils/iFrameHelper';

const POLLING_INTERVAL_MS = config.isProduction ? 45_000 : 10_000;

export const mergeNotifications = (
    previousNotifications: Notification[],
    nextNotifications: Notification[]
): Notification[] => {
    // Merge the new notifications with the previous ones and make sure that previously
    // fetched notifications are updated for instance when their status changed
    const notificationsMap = new Map(
        [...previousNotifications, ...nextNotifications].map(notification => [notification.id, notification])
    );

    return [...notificationsMap.values()];
};

const getImportance = (notifications: Notification[] | undefined) => {
    const importanceSet = new Set(
        notifications
            ?.filter(notification => notification.state === 'NEW')
            ?.map(notification => notification.importance)
    );
    if (importanceSet.has('HIGH')) {
        return 'HIGH';
    }
    if (importanceSet.has('MEDIUM')) {
        return 'MEDIUM';
    }
    if (importanceSet.size > 0) {
        return 'LOW';
    }
    return 'LOW';
};

export const useNotifications = (isVehicleMode = false) => {
    const notifications = useFetchedNotifications();
    const afterTag = useAfterTag();
    const hasNext = useHasNext();

    const [latestTimestamp, setLatestTimestamp] = useState<string | undefined>();

    const userId = useUserId();
    const language = useLocale();

    const externalRefetchTrigger = useExternalRefetchTrigger();

    let { vehicleId: entitySourceId, notifyParentOnChange } = useRemoteMessage(isVehicleMode);

    // Due to the remote vehicle hook, we have to reset the entitySourceId in case it is used
    // inside the Livemonitor but inside the notification bell so the notifications are not filtered
    if (!isVehicleMode) {
        entitySourceId = undefined;
    }

    // When not in vehicle mode, means running inside the header, the polling should be enabled by default.
    // When inside the Livemonitor sidebar an asset ID is provided, then start the polling as long the vehicle is selected.
    const shouldPoll = !isVehicleMode || Boolean(entitySourceId);

    const { isLoading, refetch } = useGetNotificationsQuery(
        { limit: NOTIFICATION_API_LIMIT, language, entitySourceId },
        {
            skip: isVehicleMode && !entitySourceId, // don’t subscribe if no vehicle - completely disables query in this case
            pollingInterval: shouldPoll ? POLLING_INTERVAL_MS : undefined, // Poll only when enabled
            refetchOnFocus: true,
        }
    );

    // Lazy query for "load more"
    const [fetchMore, { isFetching: isFetchMoreLoading, isSuccess: isFetchMoreSuccess }] =
        useLazyGetNotificationsAfterQuery();
    const fetchMoreNotifications = () => {
        if (afterTag) {
            fetchMore({
                limit: NOTIFICATION_API_LIMIT,
                after: afterTag,
                language,
                entitySourceId,
            });
        }
    };

    // Update the new notification count to be shown in the header for the notification bell
    const newNotificationsCount = notifications?.filter(notification => notification.state === 'NEW').length ?? 0;

    // Update the importance level for the notification bell in the header. This is need to show either an error badge
    // when at least one notification is HIGH or change to a warning badge when there is no HIGH but MEDIUM new notifications.
    // It is also used to remove the badge when there is no new or only LOW notifications.
    const notificationLevel = getImportance(notifications);

    // Send this event only when used inside the header and notifications aren't filtered by asset ID
    useEffect(() => {
        if (!isVehicleMode) {
            dispatchMsgToParentIFrame(notificationCount(newNotificationsCount));
            dispatchMsgToParentIFrame(maxNotificationLevel(notificationLevel));
        }
    }, [newNotificationsCount, notificationLevel, isVehicleMode, notifications]);

    // Update the latest read timestamp using the newest fetched notification, regardless of its read state.
    // This ensures that only the currently fetched notifications are marked as read.
    // If we used the timestamp of the button click instead, any notifications created during the polling interval
    // could be incorrectly marked as read.
    useEffect(() => setLatestTimestamp(getLatestTimestampIgnoreReadStatus(notifications)), [notifications]);

    // Refetch notifications again, when an external party sends an event to update
    useEffect(() => {
        if (externalRefetchTrigger) {
            console.log(externalRefetchTrigger);

            refetch();
        }
    }, [externalRefetchTrigger]);

    // Set bookmark to mark all notifications as read for the given entitySourceId or all
    const [setBookmark] = useSetBookmarkMutation();
    const [updateNotification] = useUpdateNotificationMutation();

    const updateSingleNotification = (notificationToUpdate: Notification) => {
        updateNotification(notificationToUpdate)
            .unwrap()
            .then(() => {
                console.debug(`Notification ${notificationToUpdate.id} updated`);
                refetch();
                notifyParentOnChange();
            })
            .catch(err => console.error('Update failed:', err));
    };

    const markAllAsRead = async () => {
        if (latestTimestamp) {
            await setBookmark({
                userId,
                readUntil: latestTimestamp,
                entitySourceId,
            }).unwrap();

            refetch();
            notifyParentOnChange();
        }
    };

    return {
        notifications,
        isLoading,
        newNotificationsCount,
        hasNext,
        refetch,
        fetchMore: fetchMoreNotifications,
        isFetchMoreLoading,
        isFetchMoreSuccess,
        markAllAsRead,
        updateSingleNotification,
    };
};
